import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import React from 'react';

import { TablePagination } from './TablePagination';

export interface ListPaginationBarProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Defaults to the 25/50/100 every v4 list page offers. */
  pageSizes?: readonly number[];
}

const DEFAULT_PAGE_SIZES = [25, 50, 100] as const;

/**
 * The footer under every v4 list table: rows-per-page on the left, page numbers
 * centred, and a spacer on the right that keeps the numbers centred rather than
 * drifting with the width of the rows selector.
 *
 * Each migrated page used to inline these ~45 lines, which is how the pages
 * ended up with subtly different row-selector heights and alignment.
 */
export const ListPaginationBar: React.FC<ListPaginationBarProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizes = DEFAULT_PAGE_SIZES,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: '8px' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '12px',
        color: 'text.secondary',
      }}
    >
      <label htmlFor="rows-per-page-select">Rows:</label>
      <Select
        id="rows-per-page-select"
        size="small"
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        aria-label="Rows per page"
        sx={{ fontSize: '12px', height: 26, '& .MuiSelect-select': { py: 0.25, px: 1 } }}
      >
        {pageSizes.map((size) => (
          <MenuItem key={size} value={size} sx={{ fontSize: '12px' }}>
            {size}
          </MenuItem>
        ))}
      </Select>
    </Box>
    <TablePagination
      page={page}
      pageSize={pageSize}
      total={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
    {/* Mirrors the rows selector's width so the page numbers stay centred. */}
    <Box sx={{ width: 160 }} />
  </Box>
);
